import React from "react";
import aboutHero from "../images/about-hero.jpg";
import homeHero from "../images/home-hero.jpg";
import oemDetail from "../images/oem-detail.jpg";
import fabricationDetail from "../images/fabrication-detail.jpg";
import { CardGrid, CTA, Hero, SectionHeader, SplitFeature, icons } from "../components/PremiumSections";
import SEO from "../components/SEO";

const divisions = [
  { title: "Industrial Machinery", icon: icons.industry, text: "Paper mill machinery, production equipment, industrial systems, and engineering support for capital equipment programs.", points: ["Paper mill machinery", "Production equipment", "Industrial systems"] },
  { title: "OEM Manufacturing", icon: icons.chip, text: "Custom machinery, private label manufacturing, contract production, industrial assemblies, and precision fabrication.", points: ["Low-volume builds", "Production runs", "Drawing-based manufacturing"] },
  { title: "Industrial Fabrication", icon: icons.tools, text: "Steel fabrication, machined components, rollers, shafts, drums, conveyors, frames, and custom engineered equipment.", points: ["Fabricated structures", "Machined components", "Assembly and finishing"] },
  { title: "Building Materials", icon: icons.layers, text: "Premium porcelain tile sourcing for builders, distributors, commercial projects, and private label programs.", points: ["Large format porcelain", "Commercial collections", "Container programs"] },
  { title: "Global Sourcing", icon: icons.global, text: "Supplier development, vendor qualification, quality coordination, export management, and container logistics.", points: ["Vendor qualification", "Quality documentation", "Export coordination"] },
];

const why = ["One U.S. point of contact", "Global manufacturing network", "Reliable supplier sourcing", "Engineering expertise", "Quality focused", "Responsive communication", "Long-term partnerships"];
const industries = ["Paper", "Recycling", "Manufacturing", "Industrial Plants", "Building Materials", "Construction", "Commercial Projects", "OEM Companies"];

export default function Home() {
  return (
    <main className="bg-white">
      <SEO title="DS Global Machines | OEM Manufacturing, Fabrication & Industrial Sourcing" description="DS Global Machines provides OEM manufacturing coordination, industrial fabrication sourcing, porcelain tile sourcing, paper machinery, and global procurement services for U.S. and international buyers." path="/" keywords={["OEM manufacturer", "industrial fabrication", "global sourcing company", "porcelain tile distributor", "paper machinery supplier"]} />
      <Hero
        eyebrow="International engineering and sourcing"
        title="Engineering Global Supply Chains."
        text="DS Global Machines connects manufacturers and industrial buyers through OEM manufacturing, engineered equipment, custom fabrication, global sourcing, and building material solutions."
        image={homeHero}
        actions={[{ label: "Explore Solutions", to: "/solutions" }, { label: "Request a Quote", to: "/contact/rfq" }]}
        stats={[{ value: "U.S.", label: "single point of coordination" }, { value: "Global", label: "qualified manufacturing network" }, { value: "Industrial", label: "machinery, fabrication, and materials" }]}
      />

      <section className="px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeader centered eyebrow="Business divisions" title="A broader industrial platform for engineered supply." text="From machinery and OEM assemblies to building materials and export coordination, DS Global Machines structures international sourcing around technical clarity and disciplined execution." />
          <div className="mt-14"><CardGrid items={divisions} columns="lg:grid-cols-5" /></div>
        </div>
      </section>

      <SplitFeature
        eyebrow="Why DS Global"
        title="Global reach with a technical, accountable U.S. interface."
        text="We help buyers move beyond fragmented overseas sourcing by coordinating qualified manufacturers, technical review, inspection, commercial communication, and export logistics through one responsive partner."
        image={aboutHero}
        points={why}
      />

      <section className="bg-slate-50 px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeader eyebrow="Industries we serve" title="Built for buyers who need reliability, not guesswork." text="Our work spans industrial production environments, commercial construction programs, machinery buyers, and distributor-led sourcing models." />
          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((item) => <div key={item} className="rounded border border-slate-200 bg-white px-5 py-5 text-sm font-semibold text-[#101820] shadow-sm">{item}</div>)}
          </div>
        </div>
      </section>

      <SplitFeature
        reverse
        eyebrow="Engineering-led sourcing"
        title="Premium coordination for machinery, components, and manufacturing programs."
        text="Every inquiry is handled as a technical sourcing program: define the requirement, qualify the supplier, control communication, document quality, and coordinate delivery."
        image={oemDetail}
        points={["Technical evaluation", "Quotation management", "Production monitoring", "Export documentation", "Container logistics", "Long-term supplier development"]}
      />

      <section className="bg-[#07111f] px-6 py-24 text-white">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#d6ad73]">Industrial depth</p>
            <h2 className="mt-4 text-3xl font-semibold md:text-5xl">A sourcing partner with real manufacturing fluency.</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {["Paper machinery and spare parts", "Custom OEM machinery", "Steel fabrication and assemblies", "Porcelain tile sourcing"].map((item) => <div key={item} className="rounded border border-white/10 bg-white/5 p-6 text-slate-200">{item}</div>)}
          </div>
        </div>
        <div className="mx-auto mt-14 max-w-7xl overflow-hidden rounded border border-white/10">
          <img src={fabricationDetail} alt="Industrial equipment manufacturing" className="h-[360px] w-full object-cover opacity-80" />
        </div>
      </section>

      <CTA />
    </main>
  );
}


